import { Injectable } from '@angular/core';
import { BrowserCacheLocation, Configuration, InteractionType, LogLevel, RedirectRequest } from '@azure/msal-browser';
import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { CoreAuthConfig, ArkikEnvironmentLike } from './core-auth.models';

export type CoreAuthTenantKey = 'local' | 'local-qa2' | 'local-uat-cemex' | 'local-uat-3p';

export interface CoreAuthTenantProfile {
  key: CoreAuthTenantKey;
  displayName: string;
  tenantName: string;
}

export const CORE_AUTH_TENANT_PROFILES: Readonly<Record<CoreAuthTenantKey, CoreAuthTenantProfile>> = {
  local: {
    key: 'local',
    displayName: 'Development Local',
    tenantName: 'arkikb2cdev',
  },
  'local-qa2': {
    key: 'local-qa2',
    displayName: 'QA2 Local',
    tenantName: 'arkikb2cqa2',
  },
  'local-uat-cemex': {
    key: 'local-uat-cemex',
    displayName: 'UAT Cemex Local',
    tenantName: 'arkikb2cuat',
  },
  'local-uat-3p': {
    key: 'local-uat-3p',
    displayName: 'UAT 3P Local',
    tenantName: 'arkikb2cuat',
  },
};

const resolveTenantKey = (tenantName: string): CoreAuthTenantKey => {
  const normalized = (tenantName || '').toLowerCase();

  if (normalized.includes('qa2')) {
    return 'local-qa2';
  }

  if (normalized.includes('uat')) {
    return 'local-uat-cemex';
  }

  return 'local';
};

export const createCoreAuthConfigFromArkikEnvironment = (
  environment: ArkikEnvironmentLike,
  tenantKey?: CoreAuthTenantKey,
): CoreAuthConfig => {
  const key = tenantKey ?? resolveTenantKey(environment.B2C_tenantName);
  const resolvedTenant = CORE_AUTH_TENANT_PROFILES[key];
  const t2ydApiBaseUrl = `${new URL(environment.APIM).origin}/t2yd/api/v1/`;

  return {
    isLocalWeb: environment.isLocalWeb,
    msal: {
      clientId: environment.B2C_clientId,
      tenantName: resolvedTenant.tenantName,
      signInPolicy: environment.B2C_signInPolicy,
      signInPolicy2: environment.B2C_signInPolicy2,
      redirectUri: environment.B2C_redirectUri,
      scopes: [`https://${resolvedTenant.tenantName}.onmicrosoft.com/b2c-api/b2c.read`],
      interactionType: InteractionType.Redirect,
    },
    api: {
      apimBaseUrl: environment.APIM,
      apiManagementUrl: environment.apiManagmet,
      subscriptionKey: environment.apimSubscriptionKey,
      protectedResourcePatterns: [environment.APIM, environment.apiManagmet, t2ydApiBaseUrl, 'https://localhost'],
      whitelistPatterns: ['/public', '/EdgeSettings'],
    },
    session: {
      cookieNames: {
        userGuid: environment.UserGuid,
        businessPlanGuid: environment.BusinessPlanGuid,
        productGuid: environment.ProductGuid,
        companyGuid: environment.CompanyGuid,
      },
      loginRoute: '/login',
    },
    endpoints: {
      loginUrl:
        environment.ApiLogin ?? environment.apiLogin ?? `${environment.APIM}arkik-customermanagement/v1/cum/login`,
      authUrl: environment.ApiAuth ?? environment.apiAuth ?? `${environment.APIM}arkik-customermanagement/v1/cum/auth`,
    },
  };
};

export const buildMsalAuthority = (config: CoreAuthConfig): string => {
  return `https://${config.msal.tenantName}.b2clogin.com/${config.msal.tenantName}.onmicrosoft.com/${config.msal.signInPolicy}`;
};

export const createMsalConfiguration = (config: CoreAuthConfig): Configuration => {
  const authorityDomain = config.msal.authorityDomain ?? `${config.msal.tenantName}.b2clogin.com`;
  const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

  return {
    auth: {
      clientId: config.msal.clientId,
      authority: buildMsalAuthority(config),
      knownAuthorities: [authorityDomain],
      redirectUri: config.msal.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
    system: {
      loggerOptions: {
        loggerCallback: (logLevel, message) => {
          if (logLevel === LogLevel.Error) {
            console.error(message);
          }
        },
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  };
};

export const createMsalGuardConfiguration = (config: CoreAuthConfig): MsalGuardConfiguration => {
  const authRequest: RedirectRequest = {
    scopes: config.msal.scopes ?? [],
  };

  return {
    interactionType: config.msal.interactionType ?? InteractionType.Redirect,
    authRequest,
  };
};

export const createMsalInterceptorConfiguration = (config: CoreAuthConfig): MsalInterceptorConfiguration => {
  const map = new Map<string, string[] | null>();
  const scopes = config.msal.scopes ?? [];
  const patterns = config.api.protectedResourcePatterns ?? [];

  patterns.forEach((pattern) => {
    map.set(pattern, scopes);
  });

  return {
    interactionType: config.msal.interactionType ?? InteractionType.Redirect,
    protectedResourceMap: map,
  };
};

@Injectable({
  providedIn: 'root',
})
export class CoreAuthConfigFactoryService {}
